import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { resetFilter } from "../../features/filterSlice";
import MultiTypeChart from "../../Components/charts/MultiTypeChart";
import { ButtonContain, Card, CardContain } from "../../style/Home";
import Filter from "../../Components/filters";
import Grid from "@mui/material/Grid";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import { Typography } from "@mui/material";
import { Colors } from "../../style/theme";
import {
  GetAllMvrName,
  GetParameterPerMvr,
  GetChecklistScore,
} from "../../services/analyticsService";
import { ToastContainer, toast } from 'react-toastify';
import { Loader } from "../../utils/loader";

const Analytics = () => {
  const dispatch = useDispatch();
  const filtersValue = useSelector((state) => state.filterData);
  const [filterValues, setFilterValues] = useState({});
  const [value, setValue] = React.useState(0);
  const [mvr, setMvr] = useState([]);
  const [selectedMvr, setSelectedMvr] = useState(0);
  const [mvrParameterName, setMvrParameterName] = useState([]);
  const [parametersWiseData, setParameterWiseData] = useState({});
  const [parametersWiseDataFetched, setParameterWiseDataFetched] = useState(0);
  const [checklistWiseData, setChecklistWiseData] = useState({});
  const [checklistWiseDataFetched, setChecklistWiseDataFetched] = useState(0);
  const [isDispatched, setIsDispatched] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(false);
  const [selectedParameterName, setSelectedParameterName] = useState("");
  const [loader, setLoader] = useState(false);

  useEffect(() => {
    dispatch(resetFilter());
    setIsDispatched(true);
  }, [dispatch]);

  const getAllMvrName = async () => {
    try {
      setLoader(true);
      let payload = filtersValue;

      const res = await GetAllMvrName({ payload });
      if (res.success) {
        setMvr(res?.data);
      }
    } catch (error) {
      console.log("Error on loading data: ", error);
      toast.error("Network Error");
    }finally{
      setLoader(false);
    }
  };

  const getParameterPerMvr = async (id) => {
    try {
      let payload = filtersValue;

      payload = { ...payload, mvrId: id };
      const res = await GetParameterPerMvr({ payload });
      if (res.success) {
        const parameterWiseScore = res?.data?.parameterWiseScore || [];
        setMvrParameterName(parameterWiseScore);
        const labels = parameterWiseScore.map((param) => param.parameter);
        const barData = parameterWiseScore.map(
          (item) => item.parameterPerInMvr
        );
        const lineData = parameterWiseScore.map(
          (item) => item.recivedScorePerOfParameter || 0
        );

        const parametersWiseData = {
          heading: "Parameter Wise MVR Scoring",
          labels: labels,
          barLabel: "Parameter Percentage(%) In MVR",
          barValues: barData,
          lineLabel: "Received Score Percentage(%)",
          lineValues: lineData,
        };

        setParameterWiseData(parametersWiseData);
        setParameterWiseDataFetched((prevCount) => prevCount + 1);
      }
    } catch (error) {
      console.log("Error on loading data: ", error);
      toast.error("Network Error");
    }
  };

  const getChecklistScore = async (id, parameterId) => {
    try {
      let payload = filtersValue;
      payload = { ...payload, mvrId: id, parameterId: parameterId };

      const res = await GetChecklistScore({ payload });
      if (res.success) {
        const { checklistWiseScore } = res?.data ?? {};
        const labels = checklistWiseScore.map((param) => param.checklist);
        const barData = checklistWiseScore.map(
          (item) => item.checklistPerInParameter
        );
        const lineData = checklistWiseScore.map(
          (item) => item.recivedScorePerOfChecklist || 0
        );

        const checklistWiseData = {
          heading: "Checklist Wise Parameter Scoring",
          labels: labels,
          barLabel: "Checklist Percentage(%) In Parameter",
          barValues: barData,
          lineLabel: "Received Score Percentage(%)",
          lineValues: lineData,
        };

        setChecklistWiseData(checklistWiseData);
        setChecklistWiseDataFetched((prevCount) => prevCount + 1);
      }
    } catch (error) {
      console.log("Error on loading data: ", error);
      toast.error("Network Error");
    }
  };

  useEffect(() => {
    if (isDispatched) {
      getAllMvrName();
    }
  }, [isDispatched, resetTrigger]);

  useEffect(() => {
    if (mvr.length > 0 && mvr[selectedMvr]?.mvrId) {
      getParameterPerMvr(mvr[selectedMvr]?.mvrId);
    }
  }, [mvr]);

  useEffect(() => {
    if (
      mvrParameterName.length > 0 &&
      mvrParameterName[selectedMvr]?.parameterId
    ) {
      setSelectedParameterName(mvrParameterName[selectedMvr]?.parameter);
      getChecklistScore(
        mvr[selectedMvr]?.mvrId,
        mvrParameterName[selectedMvr]?.parameterId
      );
    }
  }, [mvrParameterName]);

  const handleFilterChange = (filterKey, value) => {
    setFilterValues((prev) => ({
      ...prev, // Keep previous values
      [filterKey]: value, // Update the specific field
    }));
  };

  // tabs start
  const handleTabsChange = (event, newValue) => {
    setValue(newValue);
    setSelectedParameterName(mvrParameterName[newValue]?.parameter);
    getChecklistScore(
      mvrParameterName[newValue]?.mvrId,
      mvrParameterName[newValue]?.parameterId
    );
  };

  const handleMvrNames = (event, newValue) => {
    setSelectedMvr(newValue);
    getParameterPerMvr(mvr[newValue]?.mvrId);
  };

  const handleSearch = async () => {
    await getAllMvrName();
    if (mvr[selectedMvr]?.mvrId) {
      await getParameterPerMvr(mvr[selectedMvr]?.mvrId);
    } else {
      setParameterWiseData({});
    }

    if (mvrParameterName[selectedMvr]?.mvrId) {
      getChecklistScore(
        mvrParameterName[selectedMvr]?.mvrId,
        mvrParameterName[selectedMvr]?.parameterId
      );
    } else {
      setChecklistWiseData({});
    }

    setParameterWiseDataFetched((prev) => prev + 1);
    setChecklistWiseDataFetched((prev) => prev + 1);
  };

  const handleReset = () => {
    dispatch(resetFilter());
    setResetTrigger(Date.now());
    setTimeout(() => setResetTrigger(false), 0);
  };

  return (
    <>
      {/* chart start */}
      <CardContain>
        <Card mt={1} className="Filter_bg">
          <Grid container spacing={1}>
            <Filter
              onFilterChange={handleFilterChange}
              reset={resetTrigger}
            ></Filter>
            <Grid item xs={12}>
              <ButtonContain
                sx={{ height: "100%", alignItems: "end", gap: "5px" }}
              >
                <button className="SearchButton" onClick={handleSearch}>
                  Search
                </button>
                <button className="ResetButton" onClick={handleReset}>
                  Reset
                </button>
              </ButtonContain>
            </Grid>
          </Grid>
        </Card>
      </CardContain>
      {mvr.length > 0 ? (
        <>
          <Box>
          <Tabs
            value={selectedMvr}
            onChange={handleMvrNames}
            variant="scrollable"
            scrollButtons
            allowScrollButtonsMobile
            aria-label="scrollable force tabs example"
            sx={{
              bgcolor: "#708090",
              marginTop: "15px",
              marginBottom: "15px",
              color: "#fff",
              boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
              "& .Mui-selected.MuiButtonBase-root": {
                color: "#fff",
              },
              "& .MuiTabs-indicator": {
                backgroundColor: Colors.primary,
              },
            }}
          >
            {mvr.map((mvrDetails, index) => (
              <Tab
                label={mvrDetails.versionName}
                sx={{ color: "#fff" }}
                key={index}
              />
            ))}
          </Tabs>
          <MultiTypeChart
            key={parametersWiseDataFetched}
            heading={parametersWiseData.heading}
            labels={parametersWiseData.labels}
            barLabel={parametersWiseData.barLabel}
            barValues={parametersWiseData.barValues}
            lineLabel={parametersWiseData.lineLabel}
            lineValues={parametersWiseData.lineValues}
          />
          </Box>

          <Box>
            <Tabs
              value={value}
              onChange={handleTabsChange}
              variant="scrollable"
              scrollButtons
              allowScrollButtonsMobile
              aria-label="scrollable force tabs example"
              sx={{
                bgcolor: "#708090",
                marginTop: "15px",
                color: "#fff",
                boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
                "& .Mui-selected.MuiButtonBase-root": {
                  color: "#fff",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: Colors.primary,
                },
              }}
            >
              {mvrParameterName.map((parameter, index) => (
                <Tab
                  label={parameter.parameter}
                  sx={{ color: "#fff" }}
                  key={index}
                />
              ))}
            </Tabs>
            <Box
              sx={{
                marginTop: 5,
              }}
            >
              <Grid container spacing={2}></Grid>

              <MultiTypeChart
                key={checklistWiseDataFetched}
                heading={selectedParameterName + " Parameter Scoring"}
                labels={checklistWiseData.labels}
                barLabel={checklistWiseData.barLabel}
                barValues={checklistWiseData.barValues}
                lineLabel={checklistWiseData.lineLabel}
                lineValues={checklistWiseData.lineValues}
              />
            </Box>
          </Box>
        </>
      ) : (<Typography sx={{ textAlign: "center", color: "#ff4500", marginTop: "10px" }}>
        No Data
      </Typography>)}
      <ToastContainer />
      {loader && <Loader fullScreen={true} />}
    </>
  );
};

export default Analytics;
